// src/components/MyVotes.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import GameDetailModal from './GameDetailModal';
import GameCard from './GameCard';

const Recommendations = ({ showLogin }) => {
  const { currentUser } = useAuth();
  const [upvotedGames, setUpvotedGames] = useState([]);
  const [downvotedGames, setDownvotedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(true);
  const [recsError, setRecsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recommendationsPage, setRecommendationsPage] = useState(1);
  const [upvotedPage, setUpvotedPage] = useState(1);
  const [downvotedPage, setDownvotedPage] = useState(1);





//get recommendations


useEffect(() => {
  const fetchRecommendations = async () => {
    if (!currentUser) {
      setRecsLoading(false);
      return;
    }
    
    try {
      setRecsLoading(true);
      
      // Check if recommendations need to be regenerated
      const checkResponse = await fetch('/api/user/check-recommendations', {
        credentials: 'include'
      });
      
      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.needsRegeneration) {
          // Regenerate recommendations if needed using the non-admin endpoint
          console.log("Recommendations need regeneration, regenerating...");
          await fetch('/api/user/regenerate-recommendations', {
            method: 'POST',
            credentials: 'include',
          });
        }
      }

      const response = await fetch('/api/user/recommendations', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      setRecommendations(data);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setRecsError(err.message);
    } finally {
      setRecsLoading(false);
    }
  };

  fetchRecommendations();
}, [currentUser]);

//get upvoted games
  useEffect(() => {
    const fetchVotedGames = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/user/votes', {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch voted games');
        }

        const data = await response.json();
        setUpvotedGames(data.upvotedGames || []);
        setDownvotedGames(data.downvotedGames || []);
      } catch (err) {
        console.error('Error fetching voted games:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchVotedGames();
    }
  }, [currentUser]);

  
  //votes 
  const handleVote = (gameId, voteValue, upvotes, downvotes) => {
  // Update the recommendations list if we're on that tab
  if (activeTab === 'recommendations') {
    setRecommendations(prevRecommendations => 
      prevRecommendations.map(game => 
        game.game_id === gameId 
          ? { ...game, userVote: voteValue, upvotes, downvotes } 
          : game
      )
    );
  }
  
  // Update the upvoted games list
  setUpvotedGames(prevGames => 
    prevGames.map(game => 
      game.id === gameId || game.game_id === gameId
        ? { ...game, userVote: voteValue, upvotes, downvotes } 
        : game
    )
  );
  
  // Update the downvoted games list
  setDownvotedGames(prevGames => 
    prevGames.map(game => 
      game.id === gameId || game.game_id === gameId
        ? { ...game, userVote: voteValue, upvotes, downvotes } 
        : game
    )
  );
}


  // Close modal
  const closeGameModal = () => {
    setSelectedGame(null);
  };

  const paginateData = (data, page, itemsPerPage = 12) => {
  const startIndex = (page - 1) * itemsPerPage;
  return data.slice(startIndex, startIndex + itemsPerPage);
};

// find what a user likes ;)
const extractUserPreferences = () => {
  if (!upvotedGames || upvotedGames.length === 0) return { themes: [], mechanics: [], themeCounts: {}, mechanicCounts: {} };
  
  // Count frequency of themes and mechanics across all upvoted games
  const themeCounts = {};
  const mechanicCounts = {};
  
  upvotedGames.forEach(game => {
    // Process themes
    if (game.themes && Array.isArray(game.themes)) {
      game.themes.forEach(theme => {
        themeCounts[theme] = (themeCounts[theme] || 0) + 1;
      });
    }
    
    // Process mechanics
    if (game.mechanics && Array.isArray(game.mechanics)) {
      game.mechanics.forEach(mechanic => {
        mechanicCounts[mechanic] = (mechanicCounts[mechanic] || 0) + 1;
      });
    }
  });
  
  // Sort by frequency and get top items
  const topThemes = Object.entries(themeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(item => item[0]);
    
  const topMechanics = Object.entries(mechanicCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(item => item[0]);
  
  return { themes: topThemes, mechanics: topMechanics, themeCounts, mechanicCounts };
  };

  return (





    <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-blue-400">Recommendations</h1>
            <p className="text-gray-400 mt-1">
              Based on your votes, we've determined you like the following:
            </p>
          </div>
        </div>

        {!loading && !error && upvotedGames.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mt-1">
              {(() => {
                const preferences = extractUserPreferences();
                return (
                  <>
                    {preferences.themes.map(theme => (
                      <span key={theme} className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-sm">
                        {theme}{currentUser?.is_admin === 1 && ` (${preferences.themeCounts[theme] || 0})`}
                      </span>
                    ))}
                    {preferences.mechanics.map(mechanic => (
                      <span key={mechanic} className="bg-green-900 text-green-100 px-2 py-1 rounded-full text-sm">
                        {mechanic}{currentUser?.is_admin === 1 && ` (${preferences.mechanicCounts[mechanic] || 0})`}
                      </span>
                    ))}
                    {preferences.themes.length === 0 && preferences.mechanics.length === 0 && (
                      <span className="text-gray-500">None detected yet. Try upvoting more games!</span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}



       


        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-2">Loading your votes...</p>
          </div>
        ) : error ? (
          <div className="bg-red-900 text-red-100 p-4 rounded-lg">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-700 mb-6">
              <button
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'recommendations'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => {
                  setActiveTab('recommendations');
                  setCurrentPage(recommendationsPage);
                }}
              >
                Recommendations ({recommendations.length})
              </button>
              <button
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'upvoted'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => {
                  setActiveTab('upvoted');
                  setCurrentPage(upvotedPage);
                }}
              >
                Upvoted ({upvotedGames.length})
              </button>
              <button
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'downvoted'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => {
                  setActiveTab('downvoted');
                  setCurrentPage(downvotedPage);
                }}
              >
                Downvoted ({downvotedGames.length})
              </button>
            </div>

            {/* Game cards */}
            
            {activeTab === 'recommendations' && (
              <>
                              
                {recsLoading ? (
                  <div className="text-center py-6">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="text-gray-400 mt-2">Finding games for you...</p>
                  </div>
                ) : recsError ? (
                  <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-200">
                    <p>{recsError}</p>
                  </div>
                ) : recommendations.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {paginateData(recommendations, currentPage).map(game => (
                        <GameCard 
                          key={game.game_id}
                          game={{...game, id: game.game_id}} // This ensures game.id is always set
                          onCardClick={(game) => setSelectedGame({...game, id: game.game_id})}
                          onVote={handleVote}
                          showLogin={showLogin}
                          showVotes={true}
                         badgeContent={
                          <>
                            {/* Only show match score for admins */}
                            {currentUser?.is_admin === 1 ? (
                              <div className="text-xs mb-1 text-center">Match Score: {Math.min(1000, Math.round(game.game_score * 1))}</div>
                            ) : null}
                            
                            {/* Show actual matching elements based on user preferences with variable sizes */}
                            {(() => {
                                const userPrefs = extractUserPreferences();
                                const matchingThemes = [];
                                const matchingMechanics = [];
                                
                                // Find matching themes
                                if (game.themes && userPrefs.themes) {
                                  userPrefs.themes.forEach((theme, index) => {
                                    if (game.themes.includes(theme)) {
                                      // Add position in preference list for scaling
                                      matchingThemes.push({ value: theme, rank: index });
                                    }
                                  });
                                }
                                
                                // Find matching mechanics
                                if (game.mechanics && userPrefs.mechanics) {
                                  userPrefs.mechanics.forEach((mechanic, index) => {
                                    if (game.mechanics.includes(mechanic)) {
                                      // Add position in preference list for scaling
                                      matchingMechanics.push({ value: mechanic, rank: index });
                                    }
                                  });
                                }
                                
                                // Combine and take top 3 factors
                                const allFactors = [
                                  ...matchingThemes.map(item => ({ type: 'theme', ...item })),
                                  ...matchingMechanics.map(item => ({ type: 'mechanic', ...item }))
                                ]
                                .sort((a, b) => a.rank - b.rank)
                                .slice(0, 3);
                                

                                if (allFactors.length > 0) {
                                  allFactors.forEach((factor) => {
                                    factor.textSize = 'text-xs';
                                    factor.padding = 'px-2 py-1';
                                  });
                                }
                                
                                return (
                                  <div className="flex flex-col w-full">
                                    <div className="text-xs text-gray-400 mb-1 w-full text-center">Recommended because:</div>
                                    
                                    {allFactors.length > 0 ? (
                                      <div className="flex flex-wrap justify-center gap-2 mt-1 w-full">
                                        {allFactors.map((factor, idx) => (
                                          <span 
                                            key={idx} 
                                            className={`text-xs px-2 py-1 rounded-full ${
                                              factor.type === 'theme' 
                                                ? 'bg-blue-900/60 text-blue-100' 
                                                : 'bg-green-900/60 text-green-100'
                                            }`}
                                          >
                                            {factor.value}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400 text-center">New type of game for you!</span>
                                    )}
                                    
                                    {/* Keep existing admin debug button */}
                                    {game.debug_info && currentUser?.is_admin === 1 && (
                                      <div className="text-center mt-1">
                                        <button 
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              // First, fetch the current recommendation settings
                                              const settingsResponse = await fetch('/api/admin/recommendation-settings', {
                                                credentials: 'include'
                                              });
                                              
                                              if (!settingsResponse.ok) {
                                                throw new Error('Failed to fetch recommendation settings');
                                              }
                                              
                                              const CONFIG = await settingsResponse.json();
                                              
                                              // Fall back to defaults if any settings are missing
                                              const defaultConfig = {
                                                THEME_WEIGHT: 3,
                                                MECHANIC_WEIGHT: 4,
                                                NON_MATCHING_THEME_PENALTY: 0.5,
                                                NON_MATCHING_MECHANIC_PENALTY: 0.5,
                                                POPULARITY_BONUS_WEIGHT: 2
                                              };
                                              
                                              // Merge defaults with fetched settings
                                              const settings = {...defaultConfig, ...CONFIG};
                                              
                                              // Parse the debug info
                                              const debugData = typeof game.debug_info === 'string' 
                                                ? JSON.parse(game.debug_info) 
                                                : game.debug_info;
                                              
                                              // Create a formatted breakdown of score contributions
                                              let message = "Match Score Breakdown:\n\n";
                                              
                                              // Add theme contributions
                                              if (debugData.theme_matches && debugData.theme_matches.length > 0) {
                                                message += "🎯 Matching Themes:\n";
                                                debugData.theme_matches.forEach(theme => {
                                                  const frequency = debugData.theme_frequency[theme] || 1;
                                                  const contribution = (frequency * settings.THEME_WEIGHT).toFixed(1);
                                                  message += `  • ${theme}: +${contribution} points (frequency: ${frequency})\n`;
                                                });
                                                message += "\n";
                                              }
                                              
                                              // Add mechanic contributions
                                              if (debugData.mechanic_matches && debugData.mechanic_matches.length > 0) {
                                                message += "⚙️ Matching Mechanics:\n";
                                                debugData.mechanic_matches.forEach(mechanic => {
                                                  const frequency = debugData.mechanic_frequency[mechanic] || 1;
                                                  const contribution = (frequency * settings.MECHANIC_WEIGHT).toFixed(1);
                                                  message += `  • ${mechanic}: +${contribution} points (frequency: ${frequency})\n`;
                                                });
                                                message += "\n";
                                              }
                                              
                                              // Add penalties for non-matching elements
                                              if (debugData.non_matching_themes && debugData.non_matching_themes.length > 0) {
                                                message += "❌ Non-Matching Themes:\n";
                                                const penalty = settings.NON_MATCHING_THEME_PENALTY * debugData.non_matching_themes.length;
                                                message += `  • ${debugData.non_matching_themes.length} themes: -${penalty.toFixed(1)} points\n\n`;
                                              }
                                              
                                              if (debugData.non_matching_mechanics && debugData.non_matching_mechanics.length > 0) {
                                                message += "❌ Non-Matching Mechanics:\n";
                                                const penalty = settings.NON_MATCHING_MECHANIC_PENALTY * debugData.non_matching_mechanics.length;
                                                message += `  • ${debugData.non_matching_mechanics.length} mechanics: -${penalty.toFixed(1)} points\n\n`;
                                              }
                                              
                                              // Add popularity bonus
                                              if (debugData.upvote_ratio) {
                                                const popularityBonus = (parseFloat(debugData.upvote_ratio) * settings.POPULARITY_BONUS_WEIGHT).toFixed(1);
                                                message += `👍 Popularity Bonus: +${popularityBonus} points (upvote ratio: ${debugData.upvote_ratio})\n\n`;
                                              }
                                              
                                              // Total score
                                              message += `📊 Final Match Score: ${debugData.final_score}\n`;
                                              
                                              alert(message);
                                            } catch (err) {
                                              console.error("Error preparing match details:", err);
                                              alert("Error preparing match details: " + err.message);
                                            }
                                          }}
                                          className="text-xs text-blue-300 hover:text-blue-200"
                                        >
                                          View match details
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </>
                          }
                        />
                      ))}
                    </div>
                    
                    {/* Pagination for recommendations */}
                    {recommendations.length > 12 && (
                      <div className="flex justify-center mt-8">
                        <div className="flex items-center space-x-2">
                          <button
                            className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                            onClick={() => {
                              if (currentPage > 1) {
                                setCurrentPage(prev => prev - 1);
                                setRecommendationsPage(prev => prev - 1);
                              }
                            }}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          <span className="text-gray-400">
                            Page {currentPage} of {Math.ceil(recommendations.length / 12)}
                          </span>
                          <button
                            className={`px-3 py-1 rounded-md ${currentPage >= Math.ceil(recommendations.length / 12) ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                            onClick={() => {
                              if (currentPage < Math.ceil(recommendations.length / 12)) {
                                setCurrentPage(prev => prev + 1);
                                setRecommendationsPage(prev => prev + 1);
                              }
                            }}
                            disabled={currentPage >= Math.ceil(recommendations.length / 12)}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-6">
                    <p className="text-xl font-semibold mb-2">No recommendations yet</p>
                    <p>Try voting on some games to get personalized recommendations!</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'upvoted' && (
              <>
                {upvotedGames.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">
                    <p className="text-xl font-semibold mb-2">No upvoted games yet</p>
                    <p>When you upvote games, they'll appear here.</p>
                  </div>
                ) : (
                  <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginateData(upvotedGames, currentPage).map(game => (
                      <GameCard
                        key={game.id}
                        game={game}
                        onCardClick={setSelectedGame}
                        onVote={handleVote}
                        showLogin={showLogin}
                        showDetails={false}
                      />
                    ))}
                  </div>
                  {upvotedGames.length > 12 && (
                    <div className="flex justify-center mt-8">
                      <div className="flex items-center space-x-2">
                        <button
                          className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                          onClick={() => {
                            if (currentPage > 1) {
                              setCurrentPage(prev => prev - 1);
                              setUpvotedPage(prev => prev - 1);
                            }
                          }}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="text-gray-400">
                          Page {currentPage} of {Math.ceil(upvotedGames.length / 12)}
                        </span>
                        <button
                          className={`px-3 py-1 rounded-md ${currentPage >= Math.ceil(upvotedGames.length / 12) ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                          onClick={() => {
                            if (currentPage < Math.ceil(upvotedGames.length / 12)) {
                              setCurrentPage(prev => prev + 1);
                              setUpvotedPage(prev => prev + 1);
                            }
                          }}
                          disabled={currentPage >= Math.ceil(upvotedGames.length / 12)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </>
            )}

            {activeTab === 'downvoted' && (
                <>
                {downvotedGames.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">
                    <p className="text-xl font-semibold mb-2">No downvoted games yet</p>
                    <p>When you downvote games, they'll appear here.</p>
                  </div>
                ) : (
                  <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginateData(downvotedGames, currentPage).map(game => (
                      <GameCard
                        key={game.id}
                        game={game}
                        onCardClick={setSelectedGame}
                        onVote={handleVote}
                        showLogin={showLogin}
                        showDetails={false}
                      />
                    ))}
                  </div>
                  {downvotedGames.length > 12 && (
                    <div className="flex justify-center mt-8">
                      <div className="flex items-center space-x-2">
                        <button
                          className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                          onClick={() => {
                            if (currentPage > 1) {
                              setCurrentPage(prev => prev - 1);
                              setDownvotedPage(prev => prev - 1);
                            }
                          }}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className="text-gray-400">
                          Page {currentPage} of {Math.ceil(downvotedGames.length / 12)}
                        </span>
                        <button
                          className={`px-3 py-1 rounded-md ${currentPage >= Math.ceil(downvotedGames.length / 12) ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                          onClick={() => {
                            if (currentPage < Math.ceil(downvotedGames.length / 12)) {
                              setCurrentPage(prev => prev + 1);
                              setDownvotedPage(prev => prev + 1);
                            }
                          }}
                          disabled={currentPage >= Math.ceil(downvotedGames.length / 12)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Game Detail Modal */}
        {selectedGame && (
          <GameDetailModal 
            game={selectedGame} 
            onClose={closeGameModal} 
            darkMode={true}
          />
        )}
      </div>
    </div>
  );
};

export default Recommendations;
